import { useMemo } from 'react';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';

/**
 * Resolves PCF attribute names to their current Revit parameter names
 * by reading the live Mapping Configuration from useConfigurationStore.
 *
 * When a user changes a mapping in the Mapping Configuration page
 * (e.g. remaps "Material" from "PiX_03_Material_ENG" to "CustomMaterial"),
 * all validation quick fixes will automatically pick up the new parameter name.
 */
export function useMappedParameters() {
  const menu = useConfigurationStore((s) => s.menuConfiguration);
  const connector = useConfigurationStore((s) => s.connectorBindingConfiguration);
  const bolt = useConfigurationStore((s) => s.boltConfiguration);

  /**
   * Map from PCF attribute name → current Revit parameter name.
   * Keys must match the `parameterName` values in quickFixConfig.ts.
   */
  const pcfToRevitParam = useMemo<Record<string, string>>(() => ({
    // Menu Configuration (plain strings)
    'OBJECT-TYPE': menu.objectTypeValue,
    'SKEY': menu.sKeyValue,
    'PIPING-SPEC': menu.pipingSpecValue,
    'SYSTEM-GROUPING': menu.systemGroupingValue,

    // Connector Binding - Endpoints (FormField.value)
    'END-POINT_1': connector.endpoint1Value.value,
    'END-POINT_2': connector.endpoint2Value.value,
    'END-POINT_3': connector.endpoint3Value.value,
    'END-POINT_4': connector.endpoint4Value.value,
    'END-TYPE_1': connector.endpoint1Value.value,
    'END-TYPE_2': connector.endpoint2Value.value,
    'END-TYPE_3': connector.endpoint3Value.value,
    'END-TYPE_4': connector.endpoint4Value.value,

    // Connector Binding - BOM Columns (FormField.value)
    'Material': connector.materialValue.value,
    'DesignStd': connector.designStdValue.value,
    'PressureClass': connector.pressureClassValue.value,

    // Connector Binding - Component Settings (FormField.value)
    'INSULATION-SPEC': connector.insulationSpecValue.value,
    'INSULATIONTHICKNESS': connector.insulationThicknessValue.value,
    'TRACING-SPEC': connector.tracingSpecValue.value,
    'ITEM-DESCRIPTION': connector.itemDescriptionValue.value,

    // Bolt Configuration (FormField.value)
    'BOLT-DIA': bolt.boltDiaValue.value,
    'BOLT-DIAMETER': bolt.boltDiaValue.value,
    'BOLT-QUANTITY': bolt.boltQuantityValue.value,
    'BOLT-LENGTH': bolt.boltLengthValue.value,
    'BOLT-SET-ACTIVE': bolt.boltSetActiveValue.value,
    'BOLT-MATERIAL': bolt.boltMaterialValue.value,
    'BOLT-STANDARD': bolt.boltStandardValue.value,
  }), [menu, connector, bolt]);

  /**
   * Resolve a PCF attribute name to the Revit parameter currently mapped to it.
   * Falls back to the pcfAttributeName itself if no mapping is found
   * (so the backend can still try to use it as a direct parameter name).
   */
  const resolveRevitParam = (pcfAttributeName: string): string =>
    pcfToRevitParam[pcfAttributeName] || pcfAttributeName;

  return { pcfToRevitParam, resolveRevitParam };
}
